import { forwardRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "../assets/icon/Icon";
import { type RHFField } from "@/common/RHFWrapper";
import { Pressable } from "./Pressable";

export type PickerBtnProps = {
  label?: string;
  onClick?: () => Promise<void>;
  value?: React.ReactNode;
  classNames?: {
    wrapper?: string;
    content?: string;
    label?: string;
    value?: string;
    icon?: string;
  };
  disabled?: boolean;
} & RHFField;

export const PickerBtn = forwardRef<HTMLDivElement, PickerBtnProps>(
  ({ label, onClick, value, classNames, invalid, error, disabled }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const isActive = isOpen || value !== undefined || invalid;
    const isRequiredError = invalid && (error?.type === "required" || !value);

    return (
      <Pressable
        ref={ref}
        disabled={disabled}
        onPress={async () => {
          if (disabled) return;
          try {
            setIsOpen(true);
            await onClick?.();
            setIsOpen(false);
          } catch (err) {
            setIsOpen(false);
          }
        }}
        className={twMerge(
          "w-full rounded-xl bg-color-fill flex justify-between items-center h-14 px-4 py-2 text-color-text-3 gap-1 hover:bg-color-fill-2 active:bg-color-fill-2 cursor-pointer",
          invalid
            ? "border border-color-danger relative z-10 bg-danger-50"
            : "",
          disabled ? "bg-color-fill-2! cursor-not-allowed" : "",
          classNames?.wrapper,
        )}
      >
        <div
          className={twMerge("min-w-0 flex-1 shrink-0", classNames?.content)}
        >
          <motion.div
            className={twMerge(
              isActive
                ? "text-color-text-2 truncate leading-4 w-[120%]"
                : "truncate",
              invalid ? "text-color-danger" : "",
              classNames?.label,
            )}
            style={{ transformOrigin: "top left" }}
            animate={{
              scale: isActive ? 0.85 : 1,
            }}
            transition={{ duration: 0.1, ease: "linear" }}
          >
            {`${label}${isRequiredError ? t("v_required") : ""}`}
          </motion.div>
          <motion.div
            className={twMerge(
              "text-color-text-1 truncate",
              isRequiredError ? "text-color-text-4" : "",
              classNames?.value,
            )}
            animate={{ height: isActive ? 21 : 0 }}
            transition={{ duration: 0.1, ease: "linear" }}
          >
            {isRequiredError ? error?.message : value}
          </motion.div>
        </div>
        <Icon type="arrow" className={twMerge("shrink-0", classNames?.icon)} />
      </Pressable>
    );
  },
);
